// src/solutions.js
// ---------------------------------------------------------------------------
// DBOS SmartView — the single source of truth for which solutions exist and
// what state each tile is in for the logged-in GIDO.
//
// PRINCIPLE: what is live is shown, what is not built is not implied.
//
// This module is PURE (no React, no Firestore, no CSS). Dashboard derives a
// state per solution here, then AppCard renders it. Backend wiring for
// SmartShop / SmartMail / SmartFinance happens in later phases — this file
// is presentation-contract only.
//
// IMPORTANT — we do NOT read gido.licensed_products. That map is live but
// stale/incomplete (e.g. GIDO_IE_0001 has smartdata.enabled=false while
// SmartData is provisioned + active + granted, and it omits smartshop /
// smartfinance). We derive from the signals that are actually maintained:
//   - smartdata: smartdata_status / quota presence / grants.smartdata
//   - everything else: grants.{id}
// When Layer 0 seeds a clean licensed_products (Path A, later), this module
// becomes a thin reconciler — the callers don't change.
// ---------------------------------------------------------------------------

export const TILE_STATES = {
  NOT_OFFERED:        'not_offered',        // not in this GIDO's offer set (region) — inert, greyed
  COMING_DATED:       'coming_dated',       // announced, not built — inert + launch-date badge
  OFFERED_UNLICENSED: 'offered_unlicensed', // offered, no grant — voucher callout + "Learn more"
  LICENSED_NO_DATA:   'licensed_no_data',   // granted, empty — empty state
  LICENSED_ACTIVE:    'licensed_active',    // granted + has data — OPEN → detail
};

// Ordinal ladder, used only to clamp a derived state down to a solution's
// hard-capped maximum (see maxState). not_offered / coming_dated are handled
// before the ladder and are never produced by clamping.
const STATE_RANK = {
  not_offered:        0,
  coming_dated:       1,
  offered_unlicensed: 2,
  licensed_no_data:   3,
  licensed_active:    4,
};

// The customer-success voucher callout (Ray's copy). Rendered on
// offered_unlicensed tiles (and trial-state tiles) as the path-to-test.
export const VOUCHER_CALLOUT = {
  email: 'hello@dbos.world',
  text:
    'Interested in testing this solution? Contact our Customer Success team ' +
    'for a test voucher at hello@dbos.world.',
};

// SmartWeb official launch (Ray, 2026-09): shown now as a dated pre-announce + meetup invite.
export const SMARTWEB_LAUNCH_LABEL = 'Official launch 17 September';
export const SMARTWEB_LAUNCH_CTA = {
  text: 'Join our launch meetup - 17 Sep, 7pm Dublin (UTC+1)',
  url:  'https://luma.com/dbos-world',
};

// ---------------------------------------------------------------------------
// Solution catalogue — order = render order in the grid (briefing §3, then
// SmartWeb last as the coming pre-announce).
//
// Field notes:
//   department    — dormant seam for the future 4+2 dept grouping (SmartCom
//                   contains mail/web/shop). Not rendered yet.
//   offeredIn     — jurisdictions where the solution is offered. Undefined =
//                   offered everywhere. Present → GIDOs outside it see
//                   not_offered. (SmartFinance is DE/IE/UK/US per its briefing.)
//   maxState      — hard cap: derived state is clamped to this. SmartFinance is
//                   built-not-deployed (§11) so it can never render active.
//   pinnedState   — absolute override regardless of grants/data. SmartWeb.
//   hasDetailView — is there a real in-portal detail screen this phase? Only
//                   SmartData today. Governs whether a licensed tile is
//                   clickable (see isClickable).
// ---------------------------------------------------------------------------
export const SOLUTIONS = [
  {
    id:            'smartdata',
    name:          'SmartData',
    icon:          '✨',
    layer:         'Layer I',
    department:    'SmartCore',
    description:   'Intelligent file storage & AI categorization',
    hasDetailView: true,
  },
  {
    id:            'smartcom',
    name:          'SmartCom',
    icon:          '👥',
    layer:         'SmartCom',
    department:    'SmartCom',
    description:   'Your central contact & CRM ledger',
    blurb:
      'One place for every contact your business deals with \u2014 customers, ' +
      'suppliers, authorities \u2014 gathered from SmartShop and, soon, every other ' +
      'DBOS solution. Add and edit your own contacts, and see who came from where.',
    hasDetailView: true, // v0.0.1 live 2026-08-22: list-contacts + save-contact, grants.smartcom-gated
  },
  {
    id:            'smartshop',
    name:          'SmartShop',
    icon:          '🛒',
    layer:         'SmartCom',
    department:    'SmartCom',
    description:   'Storefront, carts & customer outreach',
    blurb:
      'Turn your files and products into a live storefront with carts and ' +
      'checkout. Reach out to customers who abandoned a cart or haven\u2019t ' +
      'ordered in a while, straight from the same place.',
    hasDetailView: true, // lifted 2026-08-06: list/get/save-product all confirmed live, grants.smartshop-gated
  },
  {
    id:            'smartmail',
    name:          'SmartMail',
    icon:          '📧',
    layer:         'SmartCom',
    department:    'SmartCom',
    description:   'White-label email — transactional & campaigns',
    blurb:
      'Send transactional and campaign email from your own verified domain \u2014 ' +
      'no third-party branding. Build lists from your existing contacts and see ' +
      'who opened, clicked, or bounced.',
    trialCode: {
      // smartmail-activate-trial — public (CORS), SMARTMAIL-BACKEND-BRIEFING §3.8.
      endpoint:    'https://europe-west1-dbos-core-prod.cloudfunctions.net/smartmail-activate-trial',
      placeholder: 'Enter code (e.g. SMT14)',
    },
    hasDetailView: false, // backend live; portal screens are Phase 4 (needs proxy)
  },
  {
    id:            'smartfinance',
    name:          'SmartFinance',
    icon:          '🪙',
    layer:         'SmartFinance',
    department:    'SmartFinance',
    description:   'Cash-basis accounting & immutable ledger',
    blurb:
      'Cash-basis bookkeeping with an immutable, audit-ready ledger. Capture ' +
      'income and expenses and keep every entry tamper-evident for tax time.',
    trialCode: {
      // sf-activate-trial-http — public, confirmed live + CORS-open 2026-07-22.
      endpoint:    'https://europe-west1-dbos-core-prod.cloudfunctions.net/sf-activate-trial-http',
      placeholder: 'Enter code (e.g. FIT14)',
    },
    offeredIn:     ['DE', 'IE', 'UK', 'US'],           // briefing §8
    // Cap lifted 2026-07-22: 4 functions live + browser-callable (D-025 §8
    // temporary deviation, closes with P4a Fri). Was: maxState LICENSED_NO_DATA.
    hasDetailView: true,
  },
  {
    id:            'smartweb',
    name:          'SmartWeb',
    icon:          '🌐',
    layer:         'SmartCom',
    department:    'SmartCom',
    description:   'Your public web presence',
    pinnedState:   TILE_STATES.COMING_DATED,            // announced, not built — hard pin
    launchLabel:   SMARTWEB_LAUNCH_LABEL,
    launchCta:     SMARTWEB_LAUNCH_CTA,
    hasDetailView: false,
  },
];

// Convenience lookup.
export const SOLUTION_BY_ID = SOLUTIONS.reduce((m, s) => { m[s.id] = s; return m; }, {});

// ---------------------------------------------------------------------------
// isGranted — did this GIDO get a grant for the solution? (single-user era:
// the owner/steward holds all grants; multi-user seat logic is a later phase.)
// ---------------------------------------------------------------------------
function isGranted(gido, id) {
  return Boolean(gido && gido.grants && gido.grants[id]);
}

// ---------------------------------------------------------------------------
// deriveTileState(solution, ctx) → TILE_STATES value
//   ctx = { gido, quota, fileCount }
// Order of checks: pinned → not-offered(region) → per-solution ladder → clamp.
// ---------------------------------------------------------------------------
export function deriveTileState(solution, ctx = {}) {
  const { gido, quota, fileCount = 0 } = ctx;

  // 1) Absolute pin (SmartWeb) — ignores everything else.
  if (solution.pinnedState) return solution.pinnedState;

  // 2) Region offer check.
  if (solution.offeredIn) {
    const juris = (gido && (gido.jurisdiction || gido.territory_id)) || null;
    if (!juris || !solution.offeredIn.includes(juris)) {
      return TILE_STATES.NOT_OFFERED;
    }
  }

  // 3) Per-solution ladder.
  let state;
  if (solution.id === 'smartdata') {
    const active = (gido && gido.smartdata_status === 'active')
      || Boolean(quota)
      || isGranted(gido, 'smartdata');
    if (!active) {
      state = TILE_STATES.OFFERED_UNLICENSED;
    } else {
      state = fileCount > 0 ? TILE_STATES.LICENSED_ACTIVE : TILE_STATES.LICENSED_NO_DATA;
    }
  } else {
    // smartshop / smartmail / smartfinance: offered to everyone in region;
    // licensed iff granted. No live in-portal data source this phase, so a
    // granted tile is licensed_no_data (never fabricate active).
    state = isGranted(gido, solution.id)
      ? TILE_STATES.LICENSED_NO_DATA
      : TILE_STATES.OFFERED_UNLICENSED;
  }

  // 4) Hard-cap clamp (SmartFinance).
  if (solution.maxState && STATE_RANK[state] > STATE_RANK[solution.maxState]) {
    state = solution.maxState;
  }
  return state;
}

// ---------------------------------------------------------------------------
// isClickable — a tile opens a detail view only when the solution has one
// built this phase AND the GIDO is in a licensed state. SmartData opens even
// when empty (you open it to upload your first file); the others have no
// detail screen yet, so they never open from the cockpit this phase.
// ---------------------------------------------------------------------------
export function isClickable(solution, state) {
  if (!solution.hasDetailView) return false;
  return state === TILE_STATES.LICENSED_ACTIVE || state === TILE_STATES.LICENSED_NO_DATA;
}

// ---------------------------------------------------------------------------
// showVoucherCallout — where the "contact Customer Success for a voucher"
// banner belongs. Offered-but-unlicensed tiles are the path-to-test.
// ---------------------------------------------------------------------------
export function showVoucherCallout(state) {
  return state === TILE_STATES.OFFERED_UNLICENSED;
}